import stopwords from 'stopwords-en';
import { oscarDatabase } from '../config/constants';
import { getIsHandleTaken } from '../resources/Auth/services';

type CategoryKey = keyof typeof oscarDatabase.nomineeCategories;

const EXCLUDED_FILM_CATEGORIES: CategoryKey[] = [
  'documentaryFeatureFilm',
  'documentaryShortFilm',
  'animatedShortFilm',
  'liveActionShortFilm',
  'internationalFeatureFilm',
  'musicOriginalSong',
];

const STOP_WORDS = new Set(stopwords);

let getActorNames = (): string[] => {
  let names: string[] = [];

  for (let category of Object.values(oscarDatabase.nomineeCategories)) {
    if (category.type !== 'performance') continue;

    for (let nominee of Object.values(category.nominees)) {
      let peopleIds = (nominee as { peopleIds?: string[] }).peopleIds;
      if (!peopleIds) continue;

      for (let personId of peopleIds) {
        let person =
          oscarDatabase.people[personId as keyof typeof oscarDatabase.people];
        if (person?.name) {
          let nameParts = person.name.split(' ');
          let firstName = nameParts[0];
          let lastName = nameParts[nameParts.length - 1];
          if (firstName && firstName.length > 1) {
            names.push(firstName);
          }
          if (lastName && lastName.length > 1 && lastName !== firstName) {
            names.push(lastName);
          }
        }
      }
    }
  }

  return Array.from(new Set(names));
};

let getFilmWords = (): string[] => {
  let words: string[] = [];
  let excludedSet = new Set<CategoryKey>(EXCLUDED_FILM_CATEGORIES);

  for (let categoryKey of Object.keys(
    oscarDatabase.nomineeCategories,
  ) as CategoryKey[]) {
    if (excludedSet.has(categoryKey)) continue;

    let category = oscarDatabase.nomineeCategories[categoryKey];
    for (let nominee of Object.values(category.nominees)) {
      let filmId = (nominee as { filmId?: string }).filmId;
      if (!filmId) continue;

      let film =
        oscarDatabase.films[filmId as keyof typeof oscarDatabase.films];
      if (film?.title) {
        let titleWords = film.title.split(/\s+/);
        for (let word of titleWords) {
          let cleaned = word.replace(/[^a-zA-Z]/g, '').toLowerCase();
          if (cleaned && !STOP_WORDS.has(cleaned)) {
            words.push(cleaned);
          }
        }
      }
    }
  }

  return Array.from(new Set(words));
};

let actorNames: string[] | null = null;
let filmWords: string[] | null = null;

let getDictionaries = () => {
  if (!actorNames) {
    actorNames = getActorNames();
  }
  if (!filmWords) {
    filmWords = getFilmWords();
  }
  return [actorNames, filmWords];
};

let generateCandidate = (): string => {
  let [names, words] = getDictionaries();
  let name = names[Math.floor(Math.random() * names.length)];
  let word = words[Math.floor(Math.random() * words.length)].slice(0, 14);
  let digits = String(Math.floor(Math.random() * 100)).padStart(2, '0');
  return (name + word + digits).toLowerCase();
};

export let generateRandomUsername = async (): Promise<string> => {
  let maxAttempts = 50;
  let attempts = 0;

  while (attempts < maxAttempts) {
    let candidate = generateCandidate();
    attempts++;

    let isTaken = await getIsHandleTaken(candidate);

    if (!isTaken) {
      return candidate;
    }

    console.error(
      `[Username Generator] Collision detected: "${candidate}" is already taken (attempt ${attempts})`,
    );
  }

  throw new Error(
    `Failed to generate unique username after ${maxAttempts} attempts`,
  );
};
